import { useEffect, useState } from "react";
import { Pencil, Building2, Users, StickyNote, AlertTriangle } from "lucide-react";
import { getOrg, updateOrg, listOrgMemberships, updateMembership, disableMembership, type AmbassadorDashboard } from "../../../api/admin";
import { Button } from "../../primitives/Button";
import { Input } from "../../primitives/Input";
import { Select } from "../../primitives/Select";
import { NotesList } from "../NotesList";
import { MembershipsTable } from "../MembershipsTable";
import { TagsManager } from "../TagsManager";
import { Modal } from "../../primitives/Modal";
import { AddMemberToOrgModal } from "../AddMemberToOrgModal";
import { EditMembershipDrawer } from "../EditMembershipDrawer";
import { DataTablePattern } from "../../patterns/DataTablePattern";

type AmbassadorDetailsTabProps = {
  ambassadorId: string;
  dashboard: AmbassadorDashboard;
  onManageAccess?: () => void;
};

export function AmbassadorDetailsTab({
  ambassadorId,
  dashboard,
  onManageAccess,
}: AmbassadorDetailsTabProps) {
  const [org, setOrg] = useState(dashboard.organization);
  const [isEditing, setIsEditing] = useState(false);
  const [members, setMembers] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [editMembershipId, setEditMembershipId] = useState<string | null>(null);
  const [addMembershipOpen, setAddMembershipOpen] = useState(false);
  const [form, setForm] = useState({
    name: org.name || "",
    email: org.email || "",
    phone: org.phone || "",
    address_line1: org.address_line1 || "",
    address_line2: org.address_line2 || "",
    city: org.city || "",
    province: org.province || "",
    postal_code: org.postal_code || "",
    country: org.country || "CA",
    status: org.status || "active",
  });

  useEffect(() => {
    let mounted = true;
    setLoadingMembers(true);
    listOrgMemberships(ambassadorId)
      .then((data) => {
        if (!mounted) return;
        setMembers(data.items || []);
        setLoadingMembers(false);
      })
      .catch((error) => {
        console.error("Failed to load members:", error);
        if (mounted) setLoadingMembers(false);
      });
    return () => {
      mounted = false;
    };
  }, [ambassadorId]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleFieldClick = () => {
    if (!isEditing) {
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setForm({
      name: org.name || "",
      email: org.email || "",
      phone: org.phone || "",
      address_line1: org.address_line1 || "",
      address_line2: org.address_line2 || "",
      city: org.city || "",
      province: org.province || "",
      postal_code: org.postal_code || "",
      country: org.country || "CA",
      status: org.status || "active",
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      const updated = await updateOrg(ambassadorId, {
        name: form.name,
        email: form.email || undefined,
        phone: form.phone || undefined,
        address_line1: form.address_line1 || undefined,
        address_line2: form.address_line2 || undefined,
        city: form.city || undefined,
        province: form.province || undefined,
        postal_code: form.postal_code || undefined,
        country: form.country || undefined,
        status: form.status as any,
      });
      setOrg(updated);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update organization:", error);
      alert("Erreur lors de la mise à jour. Veuillez réessayer.");
    }
  };

  const handleMembershipUpdate = () => {
    listOrgMemberships(ambassadorId)
      .then((data) => {
        setMembers(data.items || []);
      })
      .catch((error) => {
        console.error("Failed to refresh members:", error);
      });
  };

  const handleSuspendMembership = async (membershipId: string) => {
    try {
      const membership = members.find((m) => m.id === membershipId);
      if (!membership) return;
      
      const newStatus = membership.status === "active" ? "suspended" : "active";
      await updateMembership(membershipId, { status: newStatus });
      handleMembershipUpdate();
    } catch (error) {
      console.error("Failed to suspend/reactivate membership:", error);
      alert("Erreur lors de la modification du statut. Veuillez réessayer.");
    }
  };

  const handleRemoveMembership = async (membershipId: string) => {
    try {
      await disableMembership(membershipId);
      handleMembershipUpdate();
    } catch (error) {
      console.error("Failed to remove membership:", error);
      alert("Erreur lors de la suppression. Veuillez réessayer.");
    }
  };

  return (
    <div className="space-y-6">
      {/* A. Informations générales */}
      <div className="rounded-md border border-border bg-surface p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-text flex items-center gap-2">
            <Building2 size={16} strokeWidth={1.5} />
            Informations générales
          </h3>
          {!isEditing ? (
            <Button
              label="Modifier"
              intent="default"
              size="sm"
              icon={<Pencil size={14} strokeWidth={1.5} />}
              onClick={handleEdit}
            />
          ) : (
            <div className="flex items-center gap-2">
              <Button label="Annuler" intent="default" size="sm" onClick={handleCancel} />
              <Button label="Enregistrer" intent="primary" size="sm" onClick={handleSave} />
            </div>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Input
              label="Nom organisation"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              readOnly={!isEditing}
              onFocus={handleFieldClick}
            />
          </div>
          <div>
            <Select
              label="Statut"
              options={["active", "suspended", "archived"]}
              value={form.status}
              onChange={(value) => setForm({ ...form, status: value })}
              readOnly={!isEditing}
              onFocus={handleFieldClick}
            />
          </div>
          <div>
            <Input
              label="Email"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              readOnly={!isEditing}
              onFocus={handleFieldClick}
            />
          </div>
          <div>
            <Input
              label="Téléphone"
              value={form.phone}
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
              readOnly={!isEditing}
              onFocus={handleFieldClick}
            />
          </div>
          <div>
            <Input
              label="Adresse"
              value={form.address_line1}
              onChange={(event) => setForm({ ...form, address_line1: event.target.value })}
              readOnly={!isEditing}
              onFocus={handleFieldClick}
            />
          </div>
          <div>
            <Input
              label="Adresse (ligne 2)"
              value={form.address_line2}
              onChange={(event) => setForm({ ...form, address_line2: event.target.value })}
              readOnly={!isEditing}
              onFocus={handleFieldClick}
            />
          </div>
          <div>
            <Input
              label="Ville"
              value={form.city}
              onChange={(event) => setForm({ ...form, city: event.target.value })}
              readOnly={!isEditing}
              onFocus={handleFieldClick}
            />
          </div>
          <div>
            <Input
              label="Province"
              value={form.province}
              onChange={(event) => setForm({ ...form, province: event.target.value })}
              readOnly={!isEditing}
              onFocus={handleFieldClick}
            />
          </div>
          <div>
            <Input
              label="Code postal"
              value={form.postal_code}
              onChange={(event) => setForm({ ...form, postal_code: event.target.value })}
              readOnly={!isEditing}
              onFocus={handleFieldClick}
            />
          </div>
          <div>
            <Input
              label="Pays"
              value={form.country}
              onChange={(event) => setForm({ ...form, country: event.target.value })}
              readOnly={!isEditing}
              onFocus={handleFieldClick}
            />
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <div className="grid gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted">Type:</span>
              <span className="text-text">Ambassadeur</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">Date d'entrée:</span>
              <span className="text-text">
                {org.created_at ? new Date(org.created_at).toLocaleDateString("fr-CA") : "—"}
              </span>
            </div>
          </div>
        </div>

        {/* Tags Section */}
        <div className="mt-4 pt-4 border-t border-border">
          <TagsManager
            organizationId={ambassadorId}
            organizationType="ambassador"
            onUpdate={handleMembershipUpdate}
          />
        </div>
      </div>

      {/* B. Équipe */}
      <div className="rounded-md border border-border bg-surface p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-text flex items-center gap-2">
            <Users size={16} strokeWidth={1.5} />
            Équipe
          </h3>
          <Button
            label="+ Ajouter un membre"
            intent="primary"
            size="sm"
            onClick={() => setAddMembershipOpen(true)}
          />
        </div>

        {loadingMembers ? (
          <div className="text-sm text-muted">Chargement...</div>
        ) : (
          <MembershipsTable
            memberships={members}
            isSuperAdmin={true}
            onEdit={(membership) => setEditMembershipId(membership.id)}
            onSuspend={handleSuspendMembership}
            onRemove={handleRemoveMembership}
          />
        )}
      </div>

      {/* C. Notes */}
      <div className="rounded-md border border-border bg-surface p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <StickyNote size={16} strokeWidth={1.5} />
          <h3 className="text-sm font-semibold text-text">Notes</h3>
        </div>
        <NotesList entityType="ambassador_org" entityId={ambassadorId} />
      </div>

      {/* D. Alertes */}
      <div className="rounded-md border border-border bg-surface p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle size={16} strokeWidth={1.5} />
          <h3 className="text-sm font-semibold text-text">Alertes</h3>
        </div>
        <div className="text-sm text-muted">
          <p>Aucune alerte ouverte pour le moment.</p>
          <p className="mt-2 text-xs">Les alertes système (retard paiement, seuil crédits, non-respect contrat) apparaîtront ici.</p>
        </div>
      </div>

      {/* Modals */}
      {addMembershipOpen && (
        <AddMemberToOrgModal
          organizationId={ambassadorId}
          open={addMembershipOpen}
          onClose={() => setAddMembershipOpen(false)}
          onSuccess={handleMembershipUpdate}
        />
      )}

      {editMembershipId && (
        <EditMembershipDrawer
          membershipId={editMembershipId}
          onClose={() => setEditMembershipId(null)}
          onSuccess={handleMembershipUpdate}
        />
      )}
    </div>
  );
}
